import { createFileRoute } from "@tanstack/react-router";
import { localProductionSection } from "@/lib/seo/local-production";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, localBusinessForCity, aeoGeo } from "@/lib/seo/schema";
import { buildRelatedHubs } from "@/lib/seo/related-hubs";
import { getCity } from "@/lib/seo/cities";
import { sundsvallTrust, sundsvallProductionPricing, sundsvallProcess } from "@/content/landing/sundsvall";

const FAQ: LandingFAQ[] = [
  {
    "q": "Vad är skillnaden mellan filmproduktion och en filmbyrå?",
    "a": "Filmproduktion är hantverket. Filmbyrå inkluderar även strategi och kreativ idé. Fenny levererar båda i samma fasta pris."
  },
  {
    "q": "Hur ser en typisk filmproduktion i Sundsvall ut?",
    "a": "Brief (v 1) → manus & storyboard (v 2) → pre-prod (v 3) → inspelning 1–3 dagar (v 3–4) → klipp, grade, ljudmix (v 4–6) → leverans."
  },
  {
    "q": "Vad kostar filmproduktion i Sundsvall 2026?",
    "a": "Social-content: 40 000–120 000 kr. Reklamfilm 15–30 s: 150 000–450 000 kr. Brand-/industrifilm: 250 000–700 000 kr. Pulse: från 14 995 kr/mån."
  },
  {
    "q": "Hur snabbt kan ni vara igång?",
    "a": "Första möte inom 5 arbetsdagar. Pulse: första klipp inom 10–14 dagar."
  },
  {
    "q": "Var i Västernorrland filmar ni?",
    "a": "Hela regionen — Birsta, Mittuniversitetet, SCA-anläggningarna, hamnen och Stenstan samt Härnösand, Timrå, Sollefteå och hela Västernorrland. Drönar-tillstånd för aktuella zoner."
  },
  {
    "q": "Vilken utrustning använder ni?",
    "a": "Sony FX6/FX9 eller ARRI Alexa Mini. Aputure/ARRI-ljus. DJI-drönare. Post i DaVinci Resolve och Premiere."
  },
  {
    "q": "Klarar ni krävande industri- och säkerhetsmiljöer?",
    "a": "Ja. Vana vid bank, skogsindustri och energi. PPE, NDA, säkerhetsbriefer och samordning med skiftledare."
  },
  {
    "q": "Tar ni hand om manus och regi?",
    "a": "Ja. Vana att översätta komplex verksamhet till säljande film."
  },
  {
    "q": "Hur många versioner ingår?",
    "a": "En master per format. Engelska versioner för export-/forskningskommunikation vid behov."
  },
  {
    "q": "Får vi raw-materialet?",
    "a": "Standard: mastrade filer + stillbilder. Raw lagras 12 månader."
  },
  {
    "q": "Kan vi vara med på set?",
    "a": "Ja. Live-feed via Frame.io för distans-kunder."
  },
  {
    "q": "Hur fungerar betalning?",
    "a": "50% vid produktionsstart, 50% vid leverans."
  }
];

const CITY = getCity("sundsvall")!;
const RELATED = buildRelatedHubs(CITY, "filmbyra");

export const Route = createFileRoute("/filmbyra_/sundsvall")({
  head: () => ({
    meta: [
      { title: "Filmbyrå Sundsvall — produktion i Västernorrland | Fenny" },
      { name: "description", content: "Filmbyrå för Sundsvall och Härnösand, Timrå, Sollefteå och hela Västernorrland. Strategi, produktion och versionering — fast pris, 4–6 v." },
      { property: "og:title", content: "Filmbyrå Sundsvall — Fenny" },
      { property: "og:description", content: "Filmbyrå för Sundsvall och Västernorrland." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/filmbyra/sundsvall" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/filmbyra/sundsvall" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/filmbyra/sundsvall", name: "Filmbyrå Sundsvall — produktion i Västernorrland | Fenny", description: "Filmbyrå för Sundsvall och Härnösand, Timrå, Sollefteå och hela Västernorrland. Strategi, produktion och versionering — fast pris, 4–6 v."})) },
      { type: "application/ld+json", children: JSON.stringify(localBusinessForCity({ city: "Sundsvall", path: "/filmbyra/sundsvall", description: "Filmbyrå för Sundsvall och Västernorrland. Strategi, produktion och versionering.", postalRegion: "Västernorrlands län" })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Filmbyrå", path: "/filmbyra" }, { name: "Sundsvall", path: "/filmbyra/sundsvall" }])) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      eyebrow="Filmbyrå · Sundsvall"
      title={<>Filmbyrån som faktiskt förstår Sundsvall och Västernorrland.</>}
      lede="Fenny är en filmbyrå med produktionsteam som producerar i Sundsvall och Härnösand, Timrå, Sollefteå och hela Västernorrland. Vi producerar reklamfilm, brand storytelling, företagsfilm och employer branding för varumärken inom bank och finans, skogsindustri (SCA, Holmen), energi och media."
      tldr={<p>Fenny är filmbyrån för <strong>Sundsvall</strong>. Vi producerar i Härnösand, Timrå, Sollefteå och hela Västernorrland. <strong>Fast pris</strong>, 4–6 v.</p>}
      ctaPrimary={{ label: "Få offert inom 48 h", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="filmbyra-sundsvall"
      trust={sundsvallTrust}
      bullets={[
        { title: "Lokalt fokus", body: "Vi producerar i Härnösand, Timrå, Sollefteå och hela Västernorrland. Resa inom regionen inkluderat." },
        { title: "Västernorrland-vana", body: "Djup erfarenhet av bank, skogsindustri och energi." },
        { title: "Hela kedjan inhouse", body: "Strategi, regi, foto, ljud, klipp, grade." },
      ]}
      pricing={{ heading: "Vad det kostar", intro: "Riktpriser för 2026.", rows: sundsvallProductionPricing, footnote: "Alla priser exkl. moms." }}
      process={{ heading: "Från brief till leverans", intro: "Linjär process med skriftligt godkännande.", steps: sundsvallProcess }}
      sections={[
        localProductionSection("sundsvall", "Sundsvall"),
        { id: "fenny-i-staden", heading: "Fenny i Sundsvall", body: <p>Vi jobbar regelbundet i Sundsvall. Skarp förplanering, koncentrerad inspelning och möjlighet att kombinera Sundsvall med Härnösand, Timrå, Sollefteå och hela Västernorrland i samma produktion.</p> },
        { id: "vem", heading: "Vem vi passar för i Västernorrland", body: <p>Vi producerar för varumärken inom bank och finans, skogsindustri (SCA, Holmen), energi och media — och levererar <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">reklamfilm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/employer-branding-film">employer branding</a> och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/kundcase-film">kundcase-film</a>.</p> },
        { id: "modeller", heading: "Hur vi levererar", body: <p>Pulse-abonnemang för löpande output, Prime för enskilda större produktioner. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/priser">Räkna pris</a>.</p> },
      ]}
      faq={FAQ}
      caseLimit={6}
      related={{
        eyebrow: "Relaterat",
        title: <>Mer för {"Sundsvall"}-team</>,
        formats: RELATED.formats,
        siblings: RELATED.siblings,
        comparisons: RELATED.comparisons,
      }}
      closing={{ title: "Söker ni filmbyrå i Sundsvall?", body: "30 minuter där vi mappar er insats och nästa steg i Västernorrland.", primary: { label: "Boka strategimöte", to: "/kontakt" }, secondary: { label: "Räkna pris", to: "/priser" } }}
    />
  );
}
